import React, { useState } from "react";
import { LayoutDashboard, WrapText, Notebook, ChartArea, BarChart, Settings, Droplets, Map, ServerCrash, ChevronLeft, ChevronRight, LogOut } from "lucide-react"; // hoặc Heroicons
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UrlCollection } from "../../../common/url-collection";
import history from "../../../common/history";
import NavBar from "./navBar";
import Drawer from "@mui/material/Drawer";
import { MediaUrl } from "../../../utils/configuration";
import SwitchLanguage from "../switchLanguage";
import { removeCookies } from "../../../utils/configuration";
import { TOKEN_KEY, REFRESH_TOKEN_KEY, URL_RETURN_KEY, FCM_TOKEN } from "../../../config/common";
import { useUserInfo } from "../../../hooks/useUserInfo";

type NavItem = {
    name: string;
    icon?: JSX.Element;
    path: string;
    subItems?: NavItem[]; // Mục con (submenu)
}

type SidebarProps = {
    open: boolean;
    setOpen: (newOpen: boolean) => void;
    toggleDrawer: (newOpen: boolean) => () => void;
    isMobile: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ open, setOpen, toggleDrawer, isMobile }) => {
    const location = useLocation();
    const currentPath = location.pathname; // Lấy đường dẫn hiện tại
    const [isCollapsed, setIsCollapsed] = useState(false); // Sidebar mặc định bị thu gọn
    const [expandedSubMenu, setExpandedSubMenu] = useState<string | null>(null); // Trạng thái menu con
    const { userInfo: user, handleUnregisterNotification } = useUserInfo();
    const { t } = useTranslation();

    const navItems: NavItem[] = [
        {
            name: t("sideBar.dashboard"),
            icon: <LayoutDashboard size={18} />,
            path: UrlCollection.Dashboard,
        },
        {
            name: t("sideBar.waterMonitoring"),
            icon: <Droplets size={18} />,
            path: UrlCollection.WaterMonitoring,
        },
        {
            name: t("sideBar.parameter"),
            icon: <WrapText size={18} />,
            path: UrlCollection.Parameter,
        },
        {
            name: t("sideBar.standard"),
            icon: <Notebook size={18} />,
            path: UrlCollection.Standard,
        },
        {
            name: t("sideBar.station"),
            icon: <ChartArea size={18} />,
            path: UrlCollection.Station,
        },
        {
            name: t("sideBar.problem"),
            icon: <ServerCrash size={18} />,
            path: UrlCollection.Problem,
        },
        {
            name: t("sideBar.map"),
            icon: <Map size={18} />,
            path: UrlCollection.Map,
        },
        {
            name: t("sideBar.statistic"),
            icon: <BarChart size={18} />,
            path: UrlCollection.Statistic,
            subItems: [
                { name: t("sideBar.statisticData"), path: UrlCollection.StatisticData },
                { name: t("sideBar.statisticMinMaxData"), path: UrlCollection.StatisticMinMaxData },
                { name: t("sideBar.statisticThresholdExceedanceData"), path: UrlCollection.StatisticThresholdExceedanceData },
                { name: t("sideBar.statisticNumberOfTimesExceededData"), path: UrlCollection.StatisticNumberOfTimesExceededData },
            ],
        },
        {
            name: t("sideBar.administrator"),
            icon: <Settings size={18} />,
            path: UrlCollection.Adminstrator,
            subItems: [
                { name: t("sideBar.userManager"), path: UrlCollection.UserManager },
                { name: t("sideBar.roles"), path: UrlCollection.Roles },
                { name: t("sideBar.parameterUnit"), path: UrlCollection.ParameterUnit },
            ],
        },
    ];

    const toggleSidebar = () => {
        setIsCollapsed((prev) => !prev);
    };

    const toggleSubMenu = (name: string) => {
        setExpandedSubMenu((prev) => (prev === name ? null : name));
    };

    const handleMenuClick = (item: NavItem) => {
        if (!item.subItems) {
            setOpen(false);
            history.push(item.path);
        }
        if (isCollapsed) {
            setIsCollapsed(false);
        }
    };

    const handleLogout = async () => {
        await handleUnregisterNotification();
        removeCookies(FCM_TOKEN);
        removeCookies("isShowDialog");
        removeCookies("isLockScreen");
        removeCookies(TOKEN_KEY);
        removeCookies(REFRESH_TOKEN_KEY);
        removeCookies(URL_RETURN_KEY);
        history.push("/login");
    };

    if (isMobile) return (
        <Drawer
            anchor="left"
            open={open}
            onClose={toggleDrawer(false)}
        >
            <div className="w-[280px] flex flex-col h-full">
                <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => {
                    history.push(UrlCollection.UserInfo);
                    setOpen(false);
                }}>
                    <img
                        src={user?.avatarUrl ? MediaUrl + user.avatarUrl : '/assets/images/user-avatar.png'}
                        alt="avatar"
                        className="w-8 h-8 rounded-full border"
                        onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/assets/images/user-avatar.png';
                        }}
                    />
                    <span className="text-sm text-gray-600">
                        <span className="font-semibold text-gray-800">{user?.fullName}</span>
                    </span>
                </div>
                <hr />
                <NavBar
                    isCollapsed={isCollapsed}
                    expandedSubMenu={expandedSubMenu}
                    handleMenuClick={handleMenuClick}
                    toggleSubMenu={toggleSubMenu}
                    currentPath={currentPath}
                    navItems={navItems}
                />
                <hr />
                <div className="my-3 mx-6 border">
                    <SwitchLanguage />
                </div>

                <div className="flex-1 flex items-end">
                    <div className="py-3 px-6 cursor-pointer bg-red-50 w-full" onClick={handleLogout}>
                        <div className="flex items-center gap-2">
                            <LogOut size={18} />
                            <span className="text-red-600">{t("sideBar.logout")}</span>
                        </div>
                    </div>
                </div>
            </div>
        </Drawer>
    )

    return (
        <aside
            className={`${isCollapsed ? "w-20" : "w-[280px]"
                } bg-white shadow-md h-full flex flex-col transition-all duration-300 ease-in-out`}
        >
            <div className="px-6 py-1 min-h-[64px] font-bold text-lg text-blue-600 border-b flex justify-between items-center">
                <div className={`${isCollapsed ? "hidden" : "block"} cursor-pointer`} onClick={() => history.push(UrlCollection.Home)}>
                    <img src="/assets/images/cems_logo_transparent.png" alt="logo" className="h-16" />
                </div>
                <button
                    onClick={toggleSidebar}
                    className="text-gray-600 focus:outline-none hover:text-gray-900 bg-gray-100 rounded-full p-1"
                >
                    {isCollapsed ? (<ChevronRight />) : (<ChevronLeft />)}
                </button>
            </div>
            <NavBar
                isCollapsed={isCollapsed}
                expandedSubMenu={expandedSubMenu}
                handleMenuClick={handleMenuClick}
                toggleSubMenu={toggleSubMenu}
                currentPath={currentPath}
                navItems={navItems}
            />
        </aside>
    )
};

export default Sidebar;
